export type ApiErrorCode =
  | 'invalid_reset_token'
  | 'rate_limited'
  | 'session_expired'
  | 'invalid_current_password'
  | 'weak_password'
  | 'password_auth_unavailable'
  | 'password_unchanged'
  | 'inactive_user'
  | 'impersonation_not_permitted'
  | 'invalid_change_password_request'
  | 'change_password_failed'
  | 'activation_invalid'
  | 'organization_served'
  | 'account_exists'
  | 'activation_unavailable'
  | 'moved_to_keycloak';
export type ApiError = Error & {
  status?: number;
  code?: ApiErrorCode;
  userMessage?: string;
  requiresLogin?: boolean;
};

const RATE_LIMIT_USER_MESSAGE = 'Too many requests right now. Try again shortly.';
export const CHANGE_PASSWORD_FALLBACK_USER_MESSAGE = 'Password change failed. Try again.';
export const CHANGE_PASSWORD_TRANSPORT_USER_MESSAGE = 'Could not reach BARK. Check your connection and try again.';
export const CHANGE_PASSWORD_SERVICE_USER_MESSAGE = 'Password change is temporarily unavailable. Try again shortly.';
export const CHANGE_PASSWORD_SUCCESS_USER_MESSAGE = 'Password changed successfully.';
export const SESSION_EXPIRED_USER_MESSAGE = 'Your session has expired. Sign in again and retry.';
export const WEAK_PASSWORD_USER_MESSAGE = 'Password must be at least 8 characters.';

/**
 * BAR-1564: a tab still on Firebase after the switch to Keycloak, when reloading did not switch it.
 * The analytics dashboard's movedToKeycloak.ts and docs/specs/keycloak-auth.md repeat it word for word.
 */
export const MOVED_TO_KEYCLOAK_USER_MESSAGE = 'BARK sign-in has moved. Reload the page to sign in.';

export function movedToKeycloakError(): ApiError {
  const error = new Error(MOVED_TO_KEYCLOAK_USER_MESSAGE) as ApiError;
  error.status = 410;
  error.code = 'moved_to_keycloak';
  error.userMessage = MOVED_TO_KEYCLOAK_USER_MESSAGE;
  return error;
}

export function terminalAuthRefreshError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.status = 401;
  error.code = 'session_expired';
  error.requiresLogin = true;
  error.userMessage = SESSION_EXPIRED_USER_MESSAGE;
  return error;
}

type ErrorPayload = {
  code?: unknown;
  detail?: unknown;
  error?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFieldValidationError(detail: unknown, field: string): boolean {
  return Array.isArray(detail) && detail.some((issue) => {
    if (!isRecord(issue)) return false;
    const location = issue.loc;
    return Array.isArray(location)
      && location.length === 2
      && location[0] === 'body'
      && location[1] === field;
  });
}

export function passwordRecoveryApiError(errorData: unknown, status: number): ApiError {
  const payload: ErrorPayload = isRecord(errorData) ? errorData : {};
  const detail = payload.detail;
  const detailText = typeof detail === 'string' ? detail.trim() : '';
  const invalidResetToken = (status === 422 && isFieldValidationError(detail, 'token'))
    || payload.code === 'invalid_reset_token'
    // TODO(BAR-893): remove this exact-string fallback once every active and
    // rollback-eligible auth revision emits the top-level code.
    || (status === 400 && detailText.toLowerCase() === 'invalid or expired token');

  let userMessage = '';
  let code: ApiErrorCode | undefined;
  if (invalidResetToken) {
    userMessage = 'Invalid or expired token';
    code = 'invalid_reset_token';
  } else if (status === 429) {
    userMessage = RATE_LIMIT_USER_MESSAGE;
    code = 'rate_limited';
  } else if (status === 422 && isFieldValidationError(detail, 'email')) {
    userMessage = 'Enter a valid email address.';
  } else if (detailText) {
    userMessage = detailText;
  } else if (typeof payload.error === 'string' && payload.error.trim()) {
    userMessage = payload.error.trim();
  }

  const error = new Error(userMessage || `Request failed (${status})`) as ApiError;
  error.status = status;
  error.userMessage = userMessage;
  if (code) error.code = code;
  return error;
}

export const ACTIVATION_INVALID_USER_MESSAGE =
  'This activation link is invalid or has expired. Register again on the website to get a new link.';
export const ORGANIZATION_SERVED_USER_MESSAGE =
  'Your organisation already uses BARK. Sign in with your company account, or ask your administrator to invite you.';
export const ACCOUNT_EXISTS_USER_MESSAGE =
  'This address already has a BARK account. Sign in, or choose "Forgot password?" on the sign-in page to set a new one.';
export const ACTIVATION_UNAVAILABLE_USER_MESSAGE =
  'Activation is temporarily unavailable. Your link still works. Try again shortly.';

/**
 * BAR-1528: `POST /signup/activate` failures, by the `detail.reason` the auth
 * service sends: 400 `activation_invalid` (unknown, used or expired link),
 * 409 `organization_served` (the address's domain belongs to a customer) and
 * 409 `account_exists` (someone made an account for the address since the
 * email went out). Anything else with a string `message` (400 `weak_password`)
 * is shown as the server wrote it.
 *
 * A 5xx keeps the form: `signup_closed` and `signup_unavailable` both leave
 * the link valid, and the auth service rolls a half-made account back, so
 * trying again is the fix.
 */
export function signupActivationApiError(errorData: unknown, status: number): ApiError {
  const payload: ErrorPayload = isRecord(errorData) ? errorData : {};
  const detail = payload.detail;
  const reason = isRecord(detail) && typeof detail.reason === 'string'
    ? detail.reason
    : (typeof payload.code === 'string' ? payload.code : '');
  const serverMessage = isRecord(detail) && typeof detail.message === 'string'
    ? detail.message.trim()
    : (typeof detail === 'string' ? detail.trim() : '');

  let userMessage: string;
  let code: ApiErrorCode | undefined;
  if (reason === 'activation_invalid' || (status === 422 && isFieldValidationError(detail, 'token'))) {
    userMessage = ACTIVATION_INVALID_USER_MESSAGE;
    code = 'activation_invalid';
  } else if (reason === 'account_exists') {
    userMessage = ACCOUNT_EXISTS_USER_MESSAGE;
    code = 'account_exists';
  } else if (reason === 'organization_served' || (status === 409 && !reason)) {
    userMessage = ORGANIZATION_SERVED_USER_MESSAGE;
    code = 'organization_served';
  } else if (status === 429) {
    userMessage = RATE_LIMIT_USER_MESSAGE;
    code = 'rate_limited';
  } else if (status === 422 && isFieldValidationError(detail, 'password')) {
    userMessage = 'Check the password and try again.';
  } else if (status >= 500) {
    userMessage = ACTIVATION_UNAVAILABLE_USER_MESSAGE;
    code = 'activation_unavailable';
  } else {
    userMessage = serverMessage || 'Could not activate your account. Try again.';
  }

  const error = new Error(userMessage) as ApiError;
  error.status = status;
  error.userMessage = userMessage;
  if (code) error.code = code;
  return error;
}

export function changePasswordApiError(
  errorData: unknown,
  status: number,
): ApiError {
  const payload: ErrorPayload = isRecord(errorData) ? errorData : {};
  const detail = payload.detail;
  const detailText = typeof detail === 'string' ? detail.trim() : '';
  const responseCode = typeof payload.code === 'string' ? payload.code : '';
  // TODO(BAR-900): remove the exact-string compatibility fallbacks below after
  // every active and rollback-eligible auth revision emits BAR-892 response
  // codes. Sources + legacy response pairs are pinned by
  // backend/auth_service/tests/test_bar892_change_password_contract.py.

  let userMessage: string;
  let code: ApiErrorCode;
  if (responseCode === 'weak_password') {
    userMessage = WEAK_PASSWORD_USER_MESSAGE;
    code = 'weak_password';
  } else if (responseCode === 'password_unchanged') {
    userMessage = 'New password must be different from current password.';
    code = 'password_unchanged';
  } else if (responseCode === 'inactive_user') {
    userMessage = 'Your account is inactive. Contact an administrator.';
    code = 'inactive_user';
  } else if (responseCode === 'impersonation_not_permitted') {
    userMessage = 'Password changes are not available while impersonating.';
    code = 'impersonation_not_permitted';
  } else if (responseCode === 'password_auth_unavailable') {
    userMessage = 'Password changes are not available for this account.';
    code = 'password_auth_unavailable';
  } else if (responseCode === 'invalid_current_password') {
    userMessage = "We couldn't verify your current password. Check it and try again.";
    code = 'invalid_current_password';
  } else if (responseCode === 'rate_limited') {
    userMessage = RATE_LIMIT_USER_MESSAGE;
    code = 'rate_limited';
  } else if (responseCode === 'session_expired' && status === 401) {
    userMessage = SESSION_EXPIRED_USER_MESSAGE;
    code = 'session_expired';
  } else if (responseCode === 'change_password_failed') {
    userMessage = status === 503
      ? CHANGE_PASSWORD_SERVICE_USER_MESSAGE
      : CHANGE_PASSWORD_FALLBACK_USER_MESSAGE;
    code = 'change_password_failed';
  } else if (status === 429) {
    userMessage = RATE_LIMIT_USER_MESSAGE;
    code = 'rate_limited';
  } else if (status === 503) {
    userMessage = CHANGE_PASSWORD_SERVICE_USER_MESSAGE;
    code = 'change_password_failed';
  } else if (status === 422 && Array.isArray(detail)) {
    userMessage = 'Check the password fields and try again.';
    code = 'invalid_change_password_request';
  } else if (
    status === 400
    && detailText.toLowerCase() === 'password must be at least 8 characters'
  ) {
    userMessage = WEAK_PASSWORD_USER_MESSAGE;
    code = 'weak_password';
  } else if (
    status === 400
    && detailText.toLowerCase() === 'new password must be different from current password'
  ) {
    userMessage = 'New password must be different from current password.';
    code = 'password_unchanged';
  } else if (status === 400 && detailText.toLowerCase() === 'inactive user') {
    userMessage = 'Your account is inactive. Contact an administrator.';
    code = 'inactive_user';
  } else if (status === 401 && detailText.toLowerCase() === 'current password is incorrect') {
    userMessage = "We couldn't verify your current password. Check it and try again.";
    code = 'invalid_current_password';
  } else if (status === 401 && detailText.toLowerCase() === 'could not validate credentials') {
    userMessage = SESSION_EXPIRED_USER_MESSAGE;
    code = 'session_expired';
  } else {
    userMessage = CHANGE_PASSWORD_FALLBACK_USER_MESSAGE;
    code = 'change_password_failed';
  }

  const error = new Error(userMessage) as ApiError;
  error.status = status;
  error.userMessage = userMessage;
  error.code = code;
  return error;
}

export function changePasswordTransportError(cause?: unknown): ApiError {
  const error = new Error(
    CHANGE_PASSWORD_TRANSPORT_USER_MESSAGE,
    cause === undefined ? undefined : { cause },
  ) as ApiError;
  error.userMessage = CHANGE_PASSWORD_TRANSPORT_USER_MESSAGE;
  return error;
}

export function changePasswordRequestError(cause: unknown): ApiError {
  const status = cause instanceof Error ? (cause as ApiError).status : undefined;
  return typeof status === 'number'
    ? changePasswordApiError({}, status)
    : changePasswordTransportError(cause);
}

// POST /admin/impersonation refuses with a code object when a switch is off,
// e.g. {"detail": {"error": "customer_org_impersonation_disabled"}}, and with a
// readable string otherwise. new Error(object) showed "[object Object]" in the
// admin dialog (2026-09-28), so the reason for the refusal was lost.
export const IMPERSONATION_DISABLED_USER_MESSAGE =
  'Impersonation is turned off in this environment.';
export const CUSTOMER_ORG_IMPERSONATION_DISABLED_USER_MESSAGE =
  'Impersonating users in customer organisations is turned off in this environment.';
export const IMPERSONATION_FAILED_USER_MESSAGE = 'Failed to start impersonation.';

const IMPERSONATION_REFUSALS: Record<string, string> = {
  impersonation_disabled: IMPERSONATION_DISABLED_USER_MESSAGE,
  customer_org_impersonation_disabled: CUSTOMER_ORG_IMPERSONATION_DISABLED_USER_MESSAGE,
};

export function impersonationApiError(errorData: unknown, status: number): ApiError {
  const payload: ErrorPayload = isRecord(errorData) ? errorData : {};
  const detail = payload.detail;
  let userMessage = IMPERSONATION_FAILED_USER_MESSAGE;
  if (typeof detail === 'string' && detail.trim()) {
    userMessage = detail.trim();
  } else if (isRecord(detail) && typeof detail.error === 'string' && detail.error) {
    userMessage = IMPERSONATION_REFUSALS[detail.error] ?? detail.error;
  }
  const error = new Error(userMessage) as ApiError;
  error.status = status;
  error.userMessage = userMessage;
  return error;
}
